import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../utils/api";
import { ApiError, ApiResponse } from "../types/api";

// ---------------------------------------------------------------------------
// Types — mirror the Django serializers
// ---------------------------------------------------------------------------

/** One physics-field override applied by a ConstructionSetting. */
export interface ConstructionModifier {
  field: string;
  op: "set_baseline" | "scale" | "add";
  value: number;
}

/** Returned by /api/construction-fields — drives the modifier-builder UI. */
export interface ConstructionFieldSpec {
  name: string;
  label: string;
  units: string;
  default: number;
  ops: Array<"set_baseline" | "scale" | "add">;
  min: number;
  max: number;
  side: string;
  help: string;
}

export interface ConstructionFieldsResponse {
  fields: ConstructionFieldSpec[];
  valid_ops: string[];
}

/** Categories must mirror ``ConstructionSetting.Category`` on the backend.
 *  Order here is the order the UI picker should render the groups. */
export const CONSTRUCTION_CATEGORIES = [
  { value: "siding", label: "Siding" },
  { value: "roofing", label: "Roofing" },
  { value: "envelope", label: "Envelope (eaves, screens, gutters)" },
  { value: "coating", label: "Coatings" },
  { value: "openings", label: "Openings (shutters, glazing)" },
  { value: "suppression", label: "Suppression" },
  { value: "interior", label: "Interior" },
  { value: "fuel", label: "Fuel load" },
  { value: "other", label: "Other" },
] as const;

export type ConstructionCategory =
  (typeof CONSTRUCTION_CATEGORIES)[number]["value"];

/**
 * Construction types (structure type + characteristics) only make sense for
 * buildings — roads, power lines etc. are never "constructed". Use this to gate
 * any construction UI/inheritance on the asset's type. Mirrors the backend
 * (`asset_row_to_dict` / `get_urban_assets_for_simulation`) and
 * `isBuildingsAssetSource` (createProject/types.ts): any asset_type containing
 * "building".
 */
export const isBuildingAssetType = (assetType: unknown): boolean =>
  typeof assetType === "string" && assetType.toLowerCase().includes("building");

/** A reusable building characteristic with one or more physics modifiers. */
export interface ConstructionSetting {
  id: number;
  name: string;
  description: string;
  category: ConstructionCategory;
  modifiers: ConstructionModifier[];
  is_system_default: boolean;
  tenant: number | null;
}

/** Embedded setting representation used inside ConstructionType.settings. */
export interface EmbeddedConstructionSetting {
  id: number;
  name: string;
  description: string;
  category: ConstructionCategory;
  modifiers: ConstructionModifier[];
  is_system_default: boolean;
  order: number;
}

/** Composed building type — an ordered collection of settings. */
export interface ConstructionType {
  id: number;
  name: string;
  description: string;
  is_system_default: boolean;
  tenant: number | null;
  settings: EmbeddedConstructionSetting[];
  /** Resolved physics overrides (keys match urban.construction_fields.FIELDS). */
  resolved_props: Record<string, number>;
}

// ---------------------------------------------------------------------------
// Field whitelist
// ---------------------------------------------------------------------------

export const useConstructionFields = () =>
  useQuery({
    queryKey: ["construction-fields"],
    queryFn: () =>
      api.request<ApiResponse<ConstructionFieldsResponse>>(
        "/api/construction-fields"
      ),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

// ---------------------------------------------------------------------------
// ConstructionSetting CRUD
// ---------------------------------------------------------------------------

export const useConstructionSettings = () =>
  useQuery({
    queryKey: ["construction-settings"],
    queryFn: () =>
      api.request<ApiResponse<ConstructionSetting[]>>(
        "/api/construction-settings"
      ),
    refetchOnWindowFocus: false,
  });

export interface ConstructionSettingPayload {
  name: string;
  description?: string;
  category: ConstructionCategory;
  modifiers: ConstructionModifier[];
}

export const useCreateConstructionSetting = () => {
  const qc = useQueryClient();
  return useMutation<
    ApiResponse<ConstructionSetting>,
    ApiError,
    ConstructionSettingPayload
  >({
    mutationFn: (body) =>
      api.request<ApiResponse<ConstructionSetting>>(
        "/api/construction-settings",
        { method: "POST", body }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["construction-settings"] });
      qc.invalidateQueries({ queryKey: ["construction-types"] });
    },
  });
};

export const useUpdateConstructionSetting = () => {
  const qc = useQueryClient();
  return useMutation<
    ApiResponse<ConstructionSetting>,
    ApiError,
    { id: number; body: Partial<ConstructionSettingPayload> }
  >({
    mutationFn: ({ id, body }) =>
      api.request<ApiResponse<ConstructionSetting>>(
        `/api/construction-settings/${id}`,
        { method: "PATCH", body }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["construction-settings"] });
      qc.invalidateQueries({ queryKey: ["construction-types"] });
    },
  });
};

export const useDeleteConstructionSetting = () => {
  const qc = useQueryClient();
  return useMutation<ApiResponse<unknown>, ApiError, number>({
    mutationFn: (id) =>
      api.request<ApiResponse<unknown>>(`/api/construction-settings/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["construction-settings"] });
      qc.invalidateQueries({ queryKey: ["construction-types"] });
    },
  });
};

// ---------------------------------------------------------------------------
// ConstructionType CRUD
// ---------------------------------------------------------------------------

export const useConstructionTypes = () =>
  useQuery({
    queryKey: ["construction-types"],
    queryFn: () =>
      api.request<ApiResponse<ConstructionType[]>>("/api/construction-types"),
    refetchOnWindowFocus: false,
  });

export interface ConstructionTypePayload {
  name: string;
  description?: string;
  /** Ordered list of setting ids that compose this type. */
  setting_ids: number[];
}

export const useCreateConstructionType = () => {
  const qc = useQueryClient();
  return useMutation<
    ApiResponse<ConstructionType>,
    ApiError,
    ConstructionTypePayload
  >({
    mutationFn: (body) =>
      api.request<ApiResponse<ConstructionType>>("/api/construction-types", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["construction-types"] });
    },
  });
};

export const useUpdateConstructionType = () => {
  const qc = useQueryClient();
  return useMutation<
    ApiResponse<ConstructionType>,
    ApiError,
    { id: number; body: Partial<ConstructionTypePayload> }
  >({
    mutationFn: ({ id, body }) =>
      api.request<ApiResponse<ConstructionType>>(
        `/api/construction-types/${id}`,
        { method: "PATCH", body }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["construction-types"] });
    },
  });
};

export const useDeleteConstructionType = () => {
  const qc = useQueryClient();
  return useMutation<ApiResponse<unknown>, ApiError, number>({
    mutationFn: (id) =>
      api.request<ApiResponse<unknown>>(`/api/construction-types/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["construction-types"] });
    },
  });
};

// ---------------------------------------------------------------------------
// Client-side helpers
// ---------------------------------------------------------------------------

/** Human label for a category value. */
export function categoryLabel(value: string): string {
  return CONSTRUCTION_CATEGORIES.find((c) => c.value === value)?.label ?? value;
}

/**
 * The construction type to pre-select wherever one is required but the user
 * hasn't chosen yet: the system "Wood" type (init_data.py seeds it), falling
 * back to any tenant type named "wood". Undefined if neither exists — callers
 * must then keep the user blocked until an explicit choice is made.
 */
export function defaultWoodConstructionType(
  types: ConstructionType[]
): ConstructionType | undefined {
  return (
    types.find((t) => t.name === "Wood" && t.is_system_default) ??
    types.find((t) => t.name.toLowerCase() === "wood")
  );
}

/**
 * Resolve a modifier stream into the final physics-override values, mirroring
 * the backend ``resolve_construction``: apply all ``set_baseline`` mods, then
 * all ``scale`` mods, then all ``add`` mods, then clamp to each field's range.
 * Used for the live preview in the type/characteristic editors.
 */
export function resolveConstructionPreview(
  modifiers: ConstructionModifier[],
  fields: ConstructionFieldSpec[]
): Record<string, number> {
  const byName = new Map(fields.map((f) => [f.name, f]));
  const values: Record<string, number> = {};
  for (const f of fields) values[f.name] = f.default;

  for (const m of modifiers)
    if (m.op === "set_baseline" && byName.has(m.field)) values[m.field] = m.value;
  for (const m of modifiers)
    if (m.op === "scale" && byName.has(m.field)) values[m.field] *= m.value;
  for (const m of modifiers)
    if (m.op === "add" && byName.has(m.field)) values[m.field] += m.value;

  for (const f of fields)
    values[f.name] = Math.max(f.min, Math.min(f.max, values[f.name]));
  return values;
}

/** Return only the fields whose resolved value differs from the default. */
export function changedFields(
  resolved: Record<string, number>,
  fields: ConstructionFieldSpec[]
): ConstructionFieldSpec[] {
  return fields.filter((f) => Math.abs(resolved[f.name] - f.default) > 1e-9);
}

/**
 * Asset construction properties arrive in two shapes:
 *  - real arrays from the GeoJSON asset endpoint (AssetSerializer), or
 *  - JSON-encoded strings from vector tiles (MVT can't carry arrays).
 * These helpers normalise both into arrays.
 */
export function asIdArray(v: unknown): number[] {
  if (Array.isArray(v)) return v.map(Number).filter((n) => !Number.isNaN(n));
  if (typeof v === "string" && v.trim()) {
    try {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed)
        ? parsed.map(Number).filter((n) => !Number.isNaN(n))
        : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function asStringArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === "string" && v.trim()) {
    try {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }
  return [];
}
